import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "./kostra";

const TOKEN_KEY = "kostra:token";
const USER_KEY = "kostra:user";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
  });
  const [username, setUsername] = useState(() => {
    try { return localStorage.getItem(USER_KEY) || null; } catch { return null; }
  });
  const [loginOpen, setLoginOpen] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(!!token);
  const pendingActionRef = useRef(null);

  const isAdmin = !!token;

  const clearSession = useCallback(() => {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch {}
    setToken(null);
    setUsername(null);
  }, []);

  // Validate stored token on mount
  useEffect(() => {
    let cancelled = false;
    if (!token) { setBootstrapping(false); return; }
    (async () => {
      try {
        await api.get("/auth/me");
        if (!cancelled) setBootstrapping(false);
      } catch {
        if (cancelled) return;
        clearSession();
        setBootstrapping(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Expose modal opener for axios 401 interceptor
  useEffect(() => {
    window.__kostraOpenLogin = () => {
      // a 401 means stored token is invalid → drop it before showing modal
      clearSession();
      setLoginOpen(true);
    };
    return () => { delete window.__kostraOpenLogin; };
  }, [clearSession]);

  const openLogin = useCallback((onSuccess) => {
    if (typeof onSuccess === "function") pendingActionRef.current = onSuccess;
    setLoginOpen(true);
  }, []);

  const closeLogin = useCallback(() => {
    setLoginOpen(false);
    pendingActionRef.current = null;
  }, []);

  const login = useCallback(async (u, p) => {
    const { data } = await api.post("/auth/login", { username: u, password: p });
    const t = data?.token;
    if (!t) throw new Error("Invalid response from server");
    try {
      localStorage.setItem(TOKEN_KEY, t);
      localStorage.setItem(USER_KEY, data.username || u);
    } catch {}
    setToken(t);
    setUsername(data.username || u);
    setLoginOpen(false);
    toast.success("Welcome back, Chef.");
    const pending = pendingActionRef.current;
    pendingActionRef.current = null;
    if (pending) {
      try { await pending(); } catch {}
    }
    return true;
  }, []);

  const logout = useCallback(() => {
    clearSession();
    toast.success("Logged out");
  }, [clearSession]);

  // Wraps a callback so that, when invoked, it requires admin.
  // If guest, opens login modal and queues the action to run after login.
  const guard = useCallback((fn, label) => {
    return (...args) => {
      if (isAdmin) return fn(...args);
      pendingActionRef.current = () => fn(...args);
      if (label) {
        toast.info(`Admin login required to ${label}.`);
      }
      setLoginOpen(true);
    };
  }, [isAdmin]);

  const value = {
    isAdmin, username, token, loginOpen, bootstrapping,
    login, logout, openLogin, closeLogin, guard,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
